/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @angular-eslint/no-outputs-metadata-property */
/* eslint-disable @angular-eslint/no-inputs-metadata-property */
import {
  Component,
  ElementRef,
  inject,
  Injector,
  ChangeDetectionStrategy,
  output,
  viewChild,
  input,
  signal,
} from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  convertCzDateToIso,
  convertSimplifiedDate,
  DomHandlerService,
  parseStringToDate,
  restrictDatepickerInput,
  simplifyDate,
  uniqueId,
} from '@shared/utils';
import { merge, NEVER } from 'rxjs';

import { DIALOG_BODY_CLASS } from '@shared/utils';
import { DatePicker, DatePickerModule } from '@openng/optimus-ui/datepicker';
import { DatepickerClearIconFixDirective } from '../directives/datepicker-clear-icon-fix.directive';
import { ControlValueAccessorConnectorComponent } from '../control-value-accessor.connector';
import { DateType } from '../datepicker/datepicker.component';
import { BaseFormControlDirective, BaseFormControlDirectiveInputs } from '../directives/base-form-control.directive';
import {
  DatePickerFormControlDirective,
  DatePickerFormControlDirectiveInputs,
  DatePickerFormControlDirectiveOutputs,
} from '../directives/datepicker-form-control.directive';
import {
  DropdownFormControlDirective,
  DropdownFormControlDirectiveInputs,
} from '../directives/dropdown-form-control.directive';
import { FormErrorComponent } from '../form-error/form-error.component';
import { FormComponent } from '../form/form.component';
import { FormConnectorService } from '../forms.utils';
import { InputWrapperComponent } from '../input-wrapper/input-wrapper.component';

const CALENDAR_OPEN_HEIGHT_PX = 490;

@Component({
  selector: 'ui-datepicker-input',
  templateUrl: './datepicker-input.component.html',
  host: { class: 'mb-4' },
  imports: [DatePickerModule, DatepickerClearIconFixDirective, ReactiveFormsModule, InputWrapperComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: DatepickerInputComponent,
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  hostDirectives: [
    {
      directive: BaseFormControlDirective,
      inputs: BaseFormControlDirectiveInputs,
    },
    {
      directive: DropdownFormControlDirective,
      inputs: DropdownFormControlDirectiveInputs,
    },
    {
      directive: DatePickerFormControlDirective,
      inputs: DatePickerFormControlDirectiveInputs,
      outputs: DatePickerFormControlDirectiveOutputs,
    },
  ],
})
export class DatepickerInputComponent extends ControlValueAccessorConnectorComponent {
  private _form = inject(FormComponent, { optional: true, skipSelf: true });
  protected _formConnectorService = inject(FormConnectorService, { optional: true, skipSelf: true });
  readonly handleChange = output<Date>();

  // sets if month should auto switch to new month if today is the last day of the month
  readonly autoSwitchMonthOnLastDay = input(false);

  readonly calendarRef = viewChild<DatePicker>('calendarRef');

  restrictInput = restrictDatepickerInput;

  submitted = false;
  randomInputId = uniqueId();
  // signál: pozice se mění i v setTimeout, kde by se bez zone.js nevykreslila
  readonly panelStyle = signal<Record<string, string>>({});

  private _el = inject(ElementRef);
  private _dom = inject(DomHandlerService);

  base = inject(BaseFormControlDirective);
  dropdown = inject(DropdownFormControlDirective);
  datePicker = inject(DatePickerFormControlDirective);

  onChange!: (val: unknown) => void;

  innerControl = new FormControl();

  constructor() {
    const injector = inject(Injector);

    super(injector);

    merge<boolean[]>(this._formConnectorService?.submitted$ ?? NEVER, this._form?.getSubmitted() ?? NEVER)
      .pipe(takeUntilDestroyed())
      .subscribe((_) => (this.submitted = _));
  }

  handleOnMonthChange() {
    const scrollTop = this._dom.htmlEl.scrollTop || 0;

    setTimeout(() => {
      if (this.isInDialog) this._dom.htmlEl.scrollTop = scrollTop;
    });

    setTimeout(() => {
      this._setTopPosition(1);
    }, 50);
  }

  // for datepickers in dialogs, we need to handle the top position differently
  setCorrectCalendarTopPosition() {
    this._setTopPosition(0);
    // TODO: The 'emit' function requires a mandatory null argument
    this.datePicker.handleShow.emit();
  }

  handleShow(): void {
    this.setCorrectCalendarTopPosition();

    if (this.autoSwitchMonthOnLastDay()) this.handleAutoSwitchMonthOnLastDay();
  }

  handleOnClose() {
    this.panelStyle.set({});
  }

  handleClear() {
    this.fireControlChange();
  }

  handleOnEnter(event: any) {
    this.processInsertExplicitDate(event.target.value);
  }

  handleAutoSwitchMonthOnLastDay() {
    const today = new Date();
    // const today = new Date(2025, 10, 30); // for test purposes also change the date in "findMaxDateMinTomorrow"
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();

    if (today.getDate() === lastDayOfMonth) {
      const calendarRef = this.calendarRef();
      if (calendarRef) {
        const mockEvent = new Event('click');
        calendarRef.navForward(mockEvent);
      }
    }
  }

  override get control(): FormControl<unknown> {
    return (this.formControl() || this.controlContainer?.control?.get(this.formControlName()!)) as FormControl<Date>;
  }

  get isInDialog() {
    const bodyEl = this._dom.bodyEl;
    return bodyEl.classList.contains(DIALOG_BODY_CLASS);
  }

  override writeValue(value: unknown): void {
    switch (this.datePicker.dataType()) {
      case DateType.DateAsString: {
        value = parseStringToDate(value as string);
        break;
      }
    }
    super.writeValue(value);
    this.innerControl.setValue(value as Date, { emitEvent: false });
  }

  override registerOnChange(fn: () => void) {
    this.onChange = fn;
  }

  fireControlChange() {
    const value = this.innerControl.value;
    this.fireHandleChange(value);
  }

  fireHandleChange(value: any) {
    switch (this.datePicker.dataType()) {
      case DateType.DateAsString: {
        if (value) {
          value = simplifyDate(value);
        } else {
          value = null;
        }
        break;
      }
    }
    this.handleChange.emit(value);
    this.onChange(value);
  }

  // #region -------------------------------------------------------- Private Methods --------------------------------------------------------
  private processInsertExplicitDate(value: string) {
    let processedValue = value;

    // Zkontrolujeme, jestli je datum ve zjednodušeném formátu (ddMMyy)
    const simplifiedDate = convertSimplifiedDate(processedValue);
    if (simplifiedDate) {
      processedValue = simplifiedDate; // Pokud je, použijeme převedený plný formát
    }

    // Převod z cz formátu na ISO
    const isoDate = convertCzDateToIso(processedValue);
    const date = isoDate ? parseStringToDate(isoDate) : null;

    this.writeValue(isoDate);
    this.fireControlChange();
  }

  private _setTopPosition(diff = 0) {
    if (!this.isInDialog) return;

    const y = (this._el.nativeElement as HTMLElement).getBoundingClientRect().top;
    const inputHeight = (this._el.nativeElement as HTMLElement).getBoundingClientRect().height;
    const panelTop = this._dom.htmlEl.scrollTop + diff;
    if (y < CALENDAR_OPEN_HEIGHT_PX)
      this.panelStyle.set({
        top: `${this.datePicker.timeOnly() ? panelTop + y + inputHeight : panelTop}px`,
      });
  }
  //#endregion ------------------------------------------------------ Private Methods --------------------------------------------------------
}
